import { apiRequest, setToken, clearAuth, getApiBaseUrl } from './apiRequest';
import { patchCurrentUser } from './session';

export const authAPI = {
  register: async (username, email, password) => {
    const data = await apiRequest<any>('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify({ username, email, password }),
    });

    if (data.token) {
      setToken(data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
    }

    return data;
  },

  /** Redirect browser to Google OAuth (call from login page). */
  startGoogleLogin: ({ returnTo }: { returnTo?: string } = {}) => {
    const base = getApiBaseUrl();
    const params = returnTo ? `?returnTo=${encodeURIComponent(returnTo)}` : '';
    window.location.href = `${base}/api/auth/google/start${params}`;
  },

  exchangeGoogleLogin: async (code) => {
    const data = await apiRequest<any>('/api/auth/google/exchange', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
    if (data.token) {
      setToken(data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
    }
    return data;
  },

  login: async (email, password) => {
    const data = await apiRequest<any>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username: email, password }),
    });
    if (data.token) {
      setToken(data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
    }
    return data;
  },

  getGoogleLoginStatus: async () => {
    const data = await apiRequest<any>('/api/auth/google/status', { method: 'GET' });
    return { configured: data.configured === true };
  },

  verify: async () => {
    return await apiRequest<any>('/api/auth/verify');
  },

  logout: () => {
    clearAuth();
  },

  getSettings: async () => {
    return await apiRequest<any>('/api/auth/settings', {
      method: 'GET',
    });
  },

  getProfile: async () => {
    return await apiRequest<any>('/api/auth/profile', { method: 'GET' });
  },

  updateProfile: async (profile: Record<string, unknown>) => {
    return await apiRequest<any>('/api/auth/profile', {
      method: 'PUT',
      body: JSON.stringify(profile),
    });
  },

  updateSettings: async (settings) => {
    const data = await apiRequest<any>('/api/auth/settings', {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
    const s = data.settings;
    if (
      s &&
      (s.githubId !== undefined || s.githubUsername !== undefined || s.authProvider !== undefined)
    ) {
      patchCurrentUser({
        githubId: s.githubId ?? null,
        githubUsername: s.githubUsername ?? null,
        ...(s.authProvider !== undefined ? { authProvider: s.authProvider } : {}),
      });
    }
    return data;
  },

  setGitHubLink: async (githubUsername) => {
    const data = await apiRequest<any>('/api/auth/github-link', {
      method: 'PUT',
      body: JSON.stringify({ githubUsername: githubUsername || null }),
    });
    if (data.githubId !== undefined || data.githubUsername !== undefined) {
      patchCurrentUser({
        githubId: data.githubId ?? null,
        githubUsername: data.githubUsername ?? null,
      });
    }
    return data;
  },

  forgotPassword: async (email) => {
    const data = await apiRequest<any>('/api/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
    return data;
  },

  resetPassword: async (token, newPassword, confirmPassword) => {
    const body: { token: any; newPassword: any; confirmPassword?: any } = { token, newPassword };
    if (confirmPassword !== undefined) body.confirmPassword = confirmPassword;
    const data = await apiRequest<any>('/api/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return data;
  },

  changePassword: async (currentPassword, newPassword, confirmPassword) => {
    const body: { currentPassword: any; newPassword: any; confirmPassword?: any } = {
      currentPassword,
      newPassword,
    };
    if (confirmPassword !== undefined) body.confirmPassword = confirmPassword;
    const data = await apiRequest<any>('/api/auth/change-password', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return data;
  },

  setPassword: async (newPassword, confirmPassword) => {
    const body: { newPassword: any; confirmPassword?: any } = { newPassword };
    if (confirmPassword !== undefined) body.confirmPassword = confirmPassword;
    const data = await apiRequest<any>('/api/auth/set-password', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return data;
  },

  /** Google Drive OAuth status for the signed-in user (api.auth.googleDrive.read). */
  getGoogleDriveStatus: async () => {
    const data = await apiRequest<any>('/api/auth/google-drive/status', { method: 'GET' });
    return {
      oauthClientConfigured: data.oauthClientConfigured === true,
      redirectUri: data.redirectUri ?? null,
      connection: data.connection ?? { connected: false, googleEmail: null, connectedAt: null },
    };
  },

  /** Returns Google authorization URL (api.auth.googleDrive.write). */
  startGoogleDriveOAuth: async ({ returnTo }: { returnTo?: string } = {}) => {
    const params = returnTo ? `?returnTo=${encodeURIComponent(returnTo)}` : '';
    const data = await apiRequest<any>(`/api/auth/google-drive/oauth/start${params}`, { method: 'GET' });
    return data.url ?? '';
  },

  disconnectGoogleDrive: async () => {
    await apiRequest<any>('/api/auth/google-drive/oauth', { method: 'DELETE' });
  },

  /** Lazy folder listing for in-app Drive browser (api.auth.googleDrive.browse). */
  browseGoogleDrive: async ({
    location = 'myDrive',
    parentId = 'root',
    driveId,
    search,
    pageToken,
    pageSize,
  }: {
    location?: string;
    parentId?: string;
    driveId?: string;
    search?: string;
    pageToken?: string;
    pageSize?: number;
  } = {}) => {
    const params = new URLSearchParams();
    params.set('location', location || 'myDrive');
    params.set('parentId', parentId || 'root');
    if (driveId) params.set('driveId', String(driveId));
    if (search && String(search).trim().length >= 2) params.set('search', String(search).trim());
    if (pageToken) params.set('pageToken', String(pageToken));
    if (pageSize != null) params.set('pageSize', String(pageSize));
    const data = await apiRequest<any>(`/api/auth/google-drive/browse?${params}`, { method: 'GET' });
    return {
      location: data.location ?? location,
      parentId: data.parentId ?? parentId,
      driveId: data.driveId ?? driveId ?? null,
      search: data.search ?? null,
      items: data.items ?? [],
      nextPageToken: data.nextPageToken ?? null,
    };
  },
};
